import type {
  ListQuestionCondition,
  ListQuestionDescriptor,
} from '../list-question-utils';

export type ListPageExcelColumn = {
  descriptors: ListQuestionDescriptor[];
};

const getGroupableCondition = (
  descriptor: ListQuestionDescriptor,
  descriptorsByName: Map<string, ListQuestionDescriptor>,
): ListQuestionCondition | undefined => {
  if (descriptor.kind !== 'list') return undefined;
  const condition = descriptor.conditions.at(-1);
  if (!condition) return undefined;
  return descriptorsByName.get(condition.questionName)?.kind === 'list' ? condition : undefined;
};

const getGroupBase = (
  descriptor: ListQuestionDescriptor,
  condition: ListQuestionCondition,
) =>
  JSON.stringify({
    parentConditions: descriptor.conditions.slice(0, -1),
    parentQuestionName: condition.questionName,
    questionTitle: descriptor.question.title,
    kind: descriptor.kind,
  });

export const getListPageExcelColumns = (
  descriptors: ListQuestionDescriptor[],
): ListPageExcelColumn[] => {
  const columns: ListPageExcelColumn[] = [];
  const columnsByGroup = new Map<string, ListPageExcelColumn>();
  const branchOccurrences = new Map<string, number>();
  const descriptorsByName = new Map(
    descriptors.map((descriptor) => [String(descriptor.question.name), descriptor]),
  );

  descriptors.forEach((descriptor) => {
    const condition = getGroupableCondition(descriptor, descriptorsByName);
    if (!condition) {
      columns.push({ descriptors: [descriptor] });
      return;
    }

    const groupBase = getGroupBase(descriptor, condition);
    const branchKey = `${groupBase}:${JSON.stringify(condition.acceptedValues)}`;
    const occurrence = branchOccurrences.get(branchKey) || 0;
    branchOccurrences.set(branchKey, occurrence + 1);
    const groupKey = `${groupBase}:${occurrence}`;
    const existingColumn = columnsByGroup.get(groupKey);
    if (existingColumn) {
      existingColumn.descriptors.push(descriptor);
      return;
    }

    const column = { descriptors: [descriptor] };
    columns.push(column);
    columnsByGroup.set(groupKey, column);
  });

  return columns;
};

export const getListPageExcelColumnDependency = (
  column: ListPageExcelColumn,
): ListQuestionCondition | undefined =>
  column.descriptors.length > 1 ? column.descriptors[0].conditions.at(-1) : undefined;

export const getListPageExcelColumnFieldName = (column: ListPageExcelColumn) =>
  column.descriptors.map(({ question }) => question.name).join('|');

export const getListPageExcelColumnTitle = (column: ListPageExcelColumn) => {
  const descriptor = column.descriptors[0];
  if (column.descriptors.length > 1 || descriptor.conditions.length === 0) {
    return descriptor.question.title;
  }
  const branch = descriptor.conditions.map(({ optionLabel }) => optionLabel).join(' / ');
  return `${descriptor.question.title} — ${branch}`;
};
